/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState, useMemo } from "react";
import DOMPurify from "dompurify";
import { useHistory } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import { Images } from "./Images";
import CustomDatePicker from "./CustomDatePicker";
import CustomSearchableDropdown from "./CustomSearchableDropdown";
import CustomCheckboxDropdown from "./CustomCheckboxDropdown";
import CustomZoneWiseSpotDropdownTickmark from "./CustomZoneWiseSpotDropdownTickmark";
import StepProgress from "./StepProgress";
import NotificationMessage from "./NotificationMessage";
import NoVehiclePopUp from "./NoVehiclePopUp.jsx";
import { getFirstname, getLastname, getEmailID } from "../../utils/SecurityService.js";
import { handleApiResponse } from "../../hooks/notificationHelper.js";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
import { useApiResponseHandler } from "../../hooks/useApiResponseHandler.js";

import { formatDate, formatDateToLong, isPastTimeInterval, toSafeFloat } from "../../utils/CommonUtils";
import {
    applyZoneSelection,
    findZoneMeta,
    getFirstZoneErrorResponse,
    getSelectedZones,
    getZoneTypeFlags,
    mergeZoneTags,
    mergeZoneTimePoints,
    mergeZoneVehicles
} from "../../utils/MultiZoneBookingUtils";
import { MapBasedBooking_api, SpotAvailability_api } from "../NewBooking/NewBookingAPI";
import { BookingStatus_api, MapBasedBooking_apiBookingStatus, PostBookingQuestion_api } from "../WeeklyCalendar/WeeklyCalendarAPI.jsx";
import PostbookingQueModal from "./PostbookingQueModal.jsx";
import loadingGIF from "../../assets/images/loading.gif"
const BookParkingOrDesk = ({ selectedTab, step, setStep, bookingInfo, setZoneCreditValue }) => {
    const { t } = useTranslation()
    const history = useHistory();
    const FirstName = getFirstname();
    const LastName = getLastname();
    const memberEmail = getEmailID();
    const [notification, setNotification] = useState(null);
    const [errors, setErrors] = useState({});
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedFrom, setSelectedFrom] = useState("09:00");
    const [selectedTo, setSelectedTo] = useState("09:30");
    const [maxDate, setMaxDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    // RON-5769: several zones can be selected; one is always kept (see applyZoneSelection).
    const [selectedZoneIds, setSelectedZoneIds] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    // SpotAvailability_api Records.Zones[]: cost, pre-booking question and zone
    // date/time per searched zone, matched to the selected space by ZoneId.
    const [zonesMeta, setZonesMeta] = useState([]);

    const vehicleRef = useRef(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    const [showSlots, setShowSlots] = useState(false);
    const [activeField, setActiveField] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const isTimeNotSelected = selectedType === 2 && (selectedFrom === "00:00" || selectedTo === "00:00" || selectedFrom === "" || selectedTo === "");

    const isTimeMissing = selectedType === 2 && (selectedFrom === "00:00" || selectedTo === "00:00" || selectedFrom === "" || selectedTo === "");
    const [showStep2Slots, setShowStep2Slots] = useState(false);
    const [timeSelectedInStep1, setTimeSelectedInStep1] = useState(false);
    const [showStep1TimeValidation, setShowStep1TimeValidation] = useState(false);
    // RON-6204: warning under the step-2 time grid after a space change cleared the range.
    const [showStep2TimeValidation, setShowStep2TimeValidation] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState("");
    const [spacesList, setSpacesList] = useState([]);
    const [cachedStep2Spaces, setCachedStep2Spaces] = useState([]);

    const [isFullyDayBlocked, setIsFullyDayBlocked] = useState(0);
    const [isRoomHasApprover, setIsRoomHasApprover] = useState(0);
    const isDisableOnSpotApi = isFullyDayBlocked === 1 || (isRoomHasApprover === 1 && isFullyDayBlocked === 1);

    //handle Postbooking Que Modal
    const [showPostbookingQueModal, setShowPostbookingQueModal] = useState(false);
    const [postbookingQueModalData, setPostbookingQueModalData] = useState([]);
    const [postRequestParams, setPostRequestParams] = useState(null);
    const [showNoVehiclePopup, setShowNoVehiclePopup] = useState(false);

    const {
        handleResponseCode,
        showAlertPopupModal,
        alertPopupModalData,
        showErrorPopupModal,
        errorPopupModalData,
        setShowAlertPopupModal,
        setShowErrorPopupModal
    } = useApiResponseHandler();
    const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
    // Every step-1 control below is merged from these (MultiZoneBookingUtils).
    const selectedZoneObjs = useMemo(
        () => getSelectedZones(bookingInfo?.ZoneList, selectedZoneIds),
        [selectedZoneIds, bookingInfo]
    );
    const hasSelectedZones = selectedZoneObjs.length > 0;

    const displaySpacesList =
        step === 1 && spacesList.length === 0 && cachedStep2Spaces.length > 0
            ? cachedStep2Spaces
            : spacesList;

    const displaySelectedDate =
        selectedDate instanceof Date && !isNaN(selectedDate.getTime())
            ? `${String(selectedDate.getDate()).padStart(2, "0")} ${t(
                selectedDate.toLocaleString("en-GB", { month: "long" })
            )} ${selectedDate.getFullYear()}`
            : "";

    // Keep only ids still in this tab's ZoneList; default to its first zone.
    useEffect(() => {
        const zoneList = bookingInfo?.ZoneList || [];
        setSelectedZoneIds((prev) => {
            const valid = prev.filter((id) => zoneList.some((z) => String(z.Id) === String(id)));
            if (valid.length > 0) return valid.length === prev.length ? prev : valid;
            return zoneList.length > 0 ? [zoneList[0].Id] : [];
        });
    }, [bookingInfo]);

    // Type: All day / Hourly once each, when any selected zone supports it.
    const typeList = useMemo(() => {

        const { hasDaily, hasHourly } = getZoneTypeFlags(selectedZoneObjs);
        const list = [];

        if (hasDaily) {
            list.push({ TypeId: 1, TypeName: t("all_day") });
        }
        if (hasHourly) {
            list.push({ TypeId: 2, TypeName: t("hourly") });
        }
        return list;

    }, [selectedZoneObjs, t]);

    // Keep the picked type while the new selection still offers it.
    useEffect(() => {
        if (typeList.length > 0) {
            setSelectedType((prev) =>
                typeList.some((type) => type.TypeId === prev) ? prev : typeList[0].TypeId
            );
        }
    }, [typeList]);

    // #region Vehicle- start
    // Union of the selected zones' vehicles. A vehicle enabled in any selected
    // zone is enabled; it is disabled only when every selected zone disables it.
    const vehicleList = useMemo(() => mergeZoneVehicles(selectedZoneObjs), [selectedZoneObjs]);

    // selected vehicle object
    const selectedVehicleObj = useMemo(() => {
        return vehicleList.find(v => v.VehiclePrimaryKey === selectedVehicle);
    }, [selectedVehicle, vehicleList]);

    const shouldDisableNextButton =
        selectedTab === 1 &&
        step === 0 &&
        hasSelectedZones &&
        vehicleList.length === 0;

    useEffect(() => {
        if (selectedTab !== 1 || step !== 0 || !hasSelectedZones) {
            setShowNoVehiclePopup(false);
            return;
        }

        if (vehicleList.length === 0) {
            setShowNoVehiclePopup(true);
            return;
        }

        setShowNoVehiclePopup(false);
    }, [selectedTab, step, hasSelectedZones, vehicleList]);

    // Bind the selected vehicle to the CURRENT zones' list (RON-5989).
    //
    // vehicleList is rebuilt from the selected zones, so this runs on first load to
    // set the default and again on every zone-selection change.
    //
    // A guard on `!selectedVehicle` only ever assigned ONCE: after a zone change the
    // previous VehiclePrimaryKey stayed selected, matched nothing in the new list,
    // and selectedVehicleObj became undefined - so the dropdown fell back to its
    // placeholder and, silently, the spot-availability and booking payloads sent
    // VehicleTypeId / FuelId / AccessibleId / ShareableId as 0 with a blank
    // CarRegistrationNumber.
    //
    // The current vehicle is kept while it is still in the list and enabled, so
    // adding a zone does not undo the user's pick. Otherwise index 0: vehicleList
    // is [...enabled, ...disabled], so it is an ENABLED vehicle whenever the
    // selection has any. A disabled one is only selected when no selected zone
    // enables any vehicle, and the validation effect below then surfaces that to
    // the user rather than it passing silently.
    useEffect(() => {
        if (selectedTab !== 1) return;

        setSelectedVehicle((prev) => {
            const current = vehicleList.find((v) => v.VehiclePrimaryKey === prev);
            if (current && !current.isDisabledVehicle) return prev;
            return vehicleList.length > 0 ? vehicleList[0].VehiclePrimaryKey : null;
        });
    }, [vehicleList, selectedTab]);

    // validate selected vehicle (runs on initial auto-select AND on change)
    useEffect(() => {
        if (selectedTab !== 1) return;
        setErrors((prev) => ({
            ...prev,
            vehicle: selectedVehicleObj?.isDisabledVehicle
                ? t("react_none_of_your_vehicles_can_be_parked_in_this_zone")
                : "",
        }));
    }, [selectedVehicleObj, selectedTab, t]);

    // #endregion Vehicle- end

    // #region Extracted tags for the selected zones- start
    const spotTagList = useMemo(() => mergeZoneTags(selectedZoneObjs), [selectedZoneObjs]);

    const toggleTag = (tagId) => {

        setSelectedTags((prev) => {

            if (prev.includes(tagId)) {
                return prev.filter(id => id !== tagId); // unselect
            }
            return [...prev, tagId]; // select
        });

    };
    // #endregion Extracted tags for the selected zone- end

    const firstLoad = useRef(true);

    useEffect(() => {

        if (firstLoad.current) {
            firstLoad.current = false;
            return;
        }
        setSelectedFrom("00:00");
        setSelectedTo("00:00");
        setShowSlots(false);
        setActiveField(null);
        setSelectedTags([]);
        setShowStep1TimeValidation(false);

    }, [selectedZoneIds]);

    // Union of the selected zones' time points, sorted.
    const zoneTimePoints = useMemo(() => mergeZoneTimePoints(selectedZoneObjs), [selectedZoneObjs]);

    useEffect(() => {

        if (!hasSelectedZones || selectedType !== 2) {
            setTimeSlots([]);
            return;
        }
        if (step === 1) {
            const sourceSpaces = spacesList.length > 0 ? spacesList : cachedStep2Spaces;
            const selectedStep2Space = sourceSpaces.find((item) => item.Id === selectedSpace);
            const step2Intervals = (selectedStep2Space?.TimeInterval || []).map((slot) => {
                const [from = "", to = ""] = String(slot).split("-");
                return `${from.trim()}-${to.trim()}`;
            });
            setTimeSlots(step2Intervals);
            return;
        }

        const intervals = zoneTimePoints;
        const slots = [];

        for (let i = 0; i < intervals.length - 1; i++) {
            slots.push(`${intervals[i]}-${intervals[i + 1]}`);
        }
        setTimeSlots(slots);

    }, [selectedType, hasSelectedZones, zoneTimePoints, step, spacesList, cachedStep2Spaces, selectedSpace]);

    const timePoints = useMemo(() => {

        if (!hasSelectedZones || selectedType !== 2) return [];

        return zoneTimePoints;

    }, [selectedType, hasSelectedZones, zoneTimePoints]);

    // #region Interval-Time slot click handler- start
    const handleSlotClick = (slot) => {

        const [from, to] = slot.split("-");
        setSelectedFrom(from);
        setSelectedTo(to);
        // ✅ Detect where selection happened
        if (step === 0) {
            setTimeSelectedInStep1(true);
        }
        //setShowStep2Slots(true); // keep visible
    };

    const normalizeStep2Slot = (slot) => String(slot || "").replace(/\s/g, "");

    const isStep2SlotDisabled = (slot) => {
        if (step !== 1 || selectedType !== 2) return false;

        const normalizedSlot = normalizeStep2Slot(slot);
        const bookedSlots = (selectedSpaceObj?.BookedSlots || []).map(normalizeStep2Slot);
        const blockedSlots = (selectedSpaceObj?.BlockedBookingTime || []).map(normalizeStep2Slot);
        const isPastSlot = isPastTimeInterval(
            slot,
            zoneCurrentTime,
            selectedDate,
            zoneCurrentDate
        );

        return bookedSlots.includes(normalizedSlot) || blockedSlots.includes(normalizedSlot) || isPastSlot;
    };

    const getSelectedSlotIndexes = () => {
        return timeSlots.reduce((indexes, currentSlot, index) => {
            const [slotStart, slotEnd] = currentSlot.split("-");

            if (
                selectedFrom &&
                selectedTo &&
                selectedFrom !== "00:00" &&
                selectedTo !== "00:00" &&
                slotOverlapsRange(slotStart, slotEnd, selectedFrom, selectedTo)
            ) {
                indexes.push(index);
            }

            return indexes;
        }, []);
    };

    const getReachableStep2Index = (fromIndex, toIndex) => {
        const direction = toIndex > fromIndex ? 1 : -1;
        let reachableIndex = fromIndex;

        for (
            let index = fromIndex + direction;
            direction > 0 ? index <= toIndex : index >= toIndex;
            index += direction
        ) {
            if (isStep2SlotDisabled(timeSlots[index])) {
                break;
            }

            reachableIndex = index;
        }

        return reachableIndex;
    };

    const handleConsecutiveSlotClick = (slot) => {

        const clickedIndex = timeSlots.indexOf(slot);

        if (clickedIndex === -1 || isStep2SlotDisabled(slot)) return;

        setShowStep2TimeValidation(false);

        const selectedIndexes = getSelectedSlotIndexes();

        if (selectedIndexes.length === 0) {
            handleSlotClick(slot);
            return;
        }

        const firstIndex = selectedIndexes[0];
        const lastIndex = selectedIndexes[selectedIndexes.length - 1];

        if (clickedIndex === firstIndex && clickedIndex === lastIndex) {
            setSelectedFrom("00:00");
            setSelectedTo("00:00");
        } else if (clickedIndex === firstIndex) {
            const [from, to] = timeSlots[firstIndex].split("-");
            setSelectedFrom(from);
            setSelectedTo(to);
        } else if (clickedIndex === lastIndex) {
            const [, to] = timeSlots[lastIndex - 1].split("-");
            setSelectedTo(to);
        } else if (clickedIndex < firstIndex) {
            const reachableIndex = getReachableStep2Index(firstIndex, clickedIndex);
            const [from] = timeSlots[reachableIndex].split("-");
            setSelectedFrom(from);
        } else if (clickedIndex > lastIndex) {
            const reachableIndex = getReachableStep2Index(lastIndex, clickedIndex);
            const [, to] = timeSlots[reachableIndex].split("-");
            setSelectedTo(to);
        } else {
            // In step 2, clicking a selected slot inside the range should keep
            // the earlier consecutive slots selected, including the clicked
            // slot, and unselect anything after it.
            const [, to] = timeSlots[clickedIndex].split("-");
            setSelectedTo(to);
        }

        if (step === 0) {
            setTimeSelectedInStep1(true);
        }
    };

    const handleTimePointClick = (time) => {
        if (activeField === "from") {
            setSelectedFrom(time);
            setShowSlots(false);
        } else if (activeField === "to") {
            setSelectedTo(time);
            setShowSlots(false);
        }

        if (step === 0) {
            setTimeSelectedInStep1(true);
        }
    };

    useEffect(() => {
        // Reset when user changes zone/date
        setTimeSelectedInStep1(false);
        setShowSlots(false);
        setActiveField(null);
        setShowStep1TimeValidation(false);
    }, [selectedZoneIds, selectedDate]);

    const toMinutes = (time) => {
        const [h, m] = time.split(":").map(Number);
        return h * 60 + m;
    };

    const isStartTimeMissing =
        selectedType === 2 &&
        (!selectedFrom || selectedFrom === "00:00") &&
        selectedTo &&
        selectedTo !== "00:00";

    const isEndTimeMissing =
        selectedType === 2 &&
        selectedFrom &&
        selectedFrom !== "00:00" &&
        (!selectedTo || selectedTo === "00:00");

    const isInvalidTimeRange =
        selectedType === 2 &&
        selectedFrom &&
        selectedTo &&
        selectedFrom !== "00:00" &&
        selectedTo !== "00:00" &&
        toMinutes(selectedTo) <= toMinutes(selectedFrom);

    const timeValidationMessage = isStartTimeMissing
        ? t("please_select_start_time")
        : isEndTimeMissing
            ? t("please_select_end_time")
            : isInvalidTimeRange
                ? t("react_end_time_must_be_after_start_time")
                : "";

    useEffect(() => {
        if (!timeValidationMessage) {
            setShowStep1TimeValidation(false);
        }
    }, [timeValidationMessage]);

    const handleStep1Next = () => {
        if (selectedTab === 1 && step === 0 && hasSelectedZones && vehicleList.length === 0) {
            setShowNoVehiclePopup(true);
            return;
        }

        if (timeValidationMessage) {
            setShowStep1TimeValidation(true);
            return;
        }

        setSelectedSpace("");
        setSpacesList([]);
        setCachedStep2Spaces([]);
        setSpotsLoading(true);
        setShowStep1TimeValidation(false);
        setShowStep2TimeValidation(false);
        setStep(1);
    };

    const handleStep2Back = () => {
        if (!timeSelectedInStep1) {
            setSelectedFrom("00:00");
            setSelectedTo("00:00");
        }

        setShowStep2TimeValidation(false);
        setStep(0);
    };

    const slotOverlapsRange = (slotStart, slotEnd, rangeStart, rangeEnd) => {

        const slotStartMin = toMinutes(slotStart);
        const slotEndMin = toMinutes(slotEnd);
        const rangeStartMin = toMinutes(rangeStart);
        const rangeEndMin = toMinutes(rangeEnd);
        return slotStartMin >= rangeStartMin && slotEndMin <= rangeEndMin;
    };

    useEffect(() => {
        if (step === 1 && isTimeNotSelected) {
            setShowStep2Slots(true);
        }
    }, [step]);

    // #endregion Interval-Time slot click handler- end

    // #region Fetch available spots when step 2 is active- start
    const [spotsLoading, setSpotsLoading] = useState(false);
    const isStep2NoSpaceAvailable =
        step === 1 &&
        !spotsLoading &&
        spacesList.length === 0 &&
        cachedStep2Spaces.length === 0;

    const fetchSpots = async () => {

        if (selectedZoneIds.length === 0) return;

        try {

            setSpotsLoading(true);

            const params = {
                BookingDate: formatDate(selectedDate),
                // Always an array, even for one zone, so the response keeps the
                // multi-zone shape (RON-5943).
                ZoneId: selectedZoneIds,
                SelectedTab: selectedTab,   // 1 for parking, 2 for desking

                FilterByStartTime: selectedFrom === "00:00" ? "" : selectedFrom,
                FilterByEndTime: selectedTo === "00:00" ? "" : selectedTo,

                FilterByTags: selectedTags.join(","),

                FilterByBookingPeriod: selectedType,

                VehicleSizeId: selectedVehicleObj?.SizeId || 0,
                AccessibleId: selectedVehicleObj?.AccessibleId || 0,
                ShareableId: selectedVehicleObj?.ShareableId || 0,
                FuelId: selectedVehicleObj?.FuelId || 0,
            };

            const response = await SpotAvailability_api(params);

            /* handleApiResponse(response, setNotification, t, {
                successMsg: 0,
                errorMsg: 1
            }); */
            handleApiUiResponse(response, {
                successToaster: 0,
                errorToaster: 1,
                alertPopup: 1
            });
            if (response?.Data?.ResponseCode !== 200) {
                setZonesMeta([]);
                setStep(0);
                return;
            }
            const records = response?.Data?.Records || {};
            const zones = records.Zones || [];
            setZonesMeta(zones);

            const dailySpots = records.AvailableDailyBookings || [];
            const hourlySpots = records.AvailableHourlyBookings || [];

            if (selectedType === 1 && dailySpots.length === 0) {
                setIsFullyDayBlocked(response?.Data?.Records?.AvailableDailyBookings?.IsFullyDayBlocked || 0);
                setIsRoomHasApprover(response?.Data?.Records?.AvailableDailyBookings?.IsRoomHasApprover || 0);
            }
            else if (selectedType === 2 && hourlySpots.length === 0) {
                setIsFullyDayBlocked(response?.Data?.Records?.AvailableHourlyBookings?.IsFullyDayBlocked || 0);
                setIsRoomHasApprover(response?.Data?.Records?.AvailableHourlyBookings?.IsRoomHasApprover || 0);
            }
            // The list for the selected period; the API leaves the other one empty.
            const spots = selectedType === 2 ? hourlySpots : dailySpots;

            // A top-level 200 can still hold zones that failed (412 / 486). Their
            // message is shown only when no zone returned a space - it then explains
            // why nothing can be booked. While another zone offers a space the user
            // can still book, so a "you already have a booking" popup would mislead.
            const zoneErrorResponse = spots.length === 0 ? getFirstZoneErrorResponse(zones) : null;
            if (zoneErrorResponse) {
                handleApiUiResponse(zoneErrorResponse, {
                    successToaster: 0,
                    errorToaster: 1,
                    alertPopup: 1
                });
            }

            setSpacesList(spots);
            if (spots.length > 0) {
                setCachedStep2Spaces(spots);
            }

        } catch (error) {

            console.error("Spot API error", error);

        } finally {

            setSpotsLoading(false);
        }

    };

    useEffect(() => {

        if (step !== 1) return;

        fetchSpots();

    }, [step]);

    useEffect(() => {
        if (displaySpacesList.length > 0) {
            const selectedSpotExists = displaySpacesList.some((item) => item.Id === selectedSpace);
            const firstSpot = selectedSpotExists
                ? displaySpacesList.find((item) => item.Id === selectedSpace)
                : displaySpacesList[0];

            if (!selectedSpotExists) {
                setSelectedSpace(firstSpot.Id);
            }
            setIsRoomHasApprover(firstSpot.IsRoomHasApprover || 0);
            setIsFullyDayBlocked(firstSpot.IsFullyDayBlocked || 0);
        }
    }, [displaySpacesList, selectedSpace]);

    const selectedSpaceObj = useMemo(() => {
        return displaySpacesList.find((item) => item.Id === selectedSpace);
    }, [displaySpacesList, selectedSpace]);

    // The zone the booking is made in: the selected space's own zone (RON-5943),
    // never the first selected zone. Before a space is picked, a single selected
    // zone is unambiguous.
    const bookingZoneId =
        selectedSpaceObj?.ZoneId ?? (selectedZoneIds.length === 1 ? selectedZoneIds[0] : null);

    const bookingZoneObj = useMemo(
        () => (bookingInfo?.ZoneList || []).find((z) => String(z.Id) === String(bookingZoneId)),
        [bookingInfo, bookingZoneId]
    );

    // Records.Zones[] entry for that zone; its fields can be null for a zone
    // that returned a business error.
    const selectedZoneMeta = useMemo(
        () => findZoneMeta(zonesMeta, bookingZoneId),
        [zonesMeta, bookingZoneId]
    );

    const individualDayCost = selectedZoneMeta?.IndividualDayCost ?? "0";
    const zoneCurrentDate = selectedZoneMeta?.ZoneCurrentDate || "";
    const zoneCurrentTime = selectedZoneMeta?.ZoneCurrentTime || "";

    // RON-6204: the range is valid for the selected space only when its slots are in
    // that space's TimeInterval, run back to back from selectedFrom to selectedTo, and
    // none is booked / blocked / past. getSelectedSlotIndexes() alone misses a range
    // the new space does not offer at all (it returns no index).
    const isSelectedRangeValidForSpace = () => {
        if (selectedType !== 2 || isTimeMissing) return false;

        const indexes = getSelectedSlotIndexes();
        if (indexes.length === 0) return false;

        const isContinuous = indexes.every((slotIndex, i) =>
            i === 0 ||
            timeSlots[indexes[i - 1]].split("-")[1] === timeSlots[slotIndex].split("-")[0]
        );
        const [rangeStart] = timeSlots[indexes[0]].split("-");
        const [, rangeEnd] = timeSlots[indexes[indexes.length - 1]].split("-");

        return (
            isContinuous &&
            rangeStart === selectedFrom &&
            rangeEnd === selectedTo &&
            !indexes.some((slotIndex) => isStep2SlotDisabled(timeSlots[slotIndex]))
        );
    };

    const isStep2RangeInvalid =
        step === 1 && selectedType === 2 && !isTimeMissing && !isSelectedRangeValidForSpace();

    // RON-5769 / RON-6204: the selected range belongs to the previous space. When the
    // space changes (often to another zone, with another "now" and other booked
    // slots), clear the range if this space does not offer all of it, so the next
    // click starts a new selection instead of extending an invalid one.
    useEffect(() => {
        if (step !== 1 || selectedType !== 2) return;
        // Wait for the selected space's slots; an empty list is "still loading".
        if (!selectedSpaceObj || timeSlots.length === 0 || isTimeMissing) return;

        if (!isSelectedRangeValidForSpace()) {
            setSelectedFrom("00:00");
            setSelectedTo("00:00");
        }
    }, [selectedSpace, timeSlots, zoneCurrentDate, zoneCurrentTime]);

    // Header credit card: the booking zone's value, else the first selected zone's.
    const zoneCreditValue = Number(
        selectedZoneMeta?.ZoneCreditValue ??
        bookingZoneObj?.ZoneCreditValue ??
        selectedZoneObjs[0]?.ZoneCreditValue ??
        0
    );

    useEffect(() => {
        if (setZoneCreditValue) {
            setZoneCreditValue(zoneCreditValue);
        }
    }, [zoneCreditValue]);


    // #endregion Fetch available spots when step 2 is active- end

    const preBookingQuestion = selectedZoneMeta?.PreBookingQuestion
        ? selectedZoneMeta.PreBookingQuestion.replace(/<\/?p>/g, "").trim()
        : "";
    const [checkedPreBookingQue, setCheckedPreBookingQue] = useState(false);

    // A space in another zone brings its own question; it must be confirmed again.
    useEffect(() => {
        setCheckedPreBookingQue(false);
    }, [preBookingQuestion]);
    const safeHtml = DOMPurify
        .sanitize(preBookingQuestion)
        .replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');

    /* const safeHtml = DOMPurify
        .sanitize(preBookingQuestion)
        .replace(/<a\b([^>]*)>/g, '<a class="w-auto" target="_blank" rel="noopener noreferrer" $1>')
        .replace(/<\/?p>/g, ""); */

    const isPreBookingQuestionChecked = Boolean(preBookingQuestion) && !checkedPreBookingQue;
    // RON-6204: a missing / invalid time does not disable Confirm. Confirm stays
    // clickable so the "select a time slot" warning appears on the attempt
    // (handleConfirmBooking), not as soon as the space changes.
    const isStep2NextDisabled =
        spotsLoading ||
        !selectedSpace ||
        isStep2NoSpaceAvailable ||
        isPreBookingQuestionChecked ||
        isDisableOnSpotApi;

    const handleConfirmBooking = async () => {

        // RON-6204: never send a space with a range it does not offer.
        if (selectedType === 2 && !isSelectedRangeValidForSpace()) {
            setShowStep2TimeValidation(true);
            return;
        }

        try {
            setIsLoading(true);
            const selectedSpaceName = selectedSpaceObj?.SpotName || "";
            const params = {
                BookingDate: formatDate(selectedDate),
                ZoneId: bookingZoneId,
                SpotId: selectedSpace,

                TeamMemberGuId: "",

                Tags: selectedTags.join(","),

                BookingPeriodStartTime: selectedFrom === "00:00" ? "" : selectedFrom,
                BookingPeriodEndTime: selectedTo === "00:00" ? "" : selectedTo,

                VehicleTypeId: selectedVehicleObj?.SizeId || 0,
                VehicleFuelId: selectedVehicleObj?.FuelId || 0,
                VehicleAccessibleId: selectedVehicleObj?.AccessibleId || 0,
                VehicleShareableId: selectedVehicleObj?.ShareableId || 0,

                // as no guest booking so below should be blank
                IsGuest: 0,
                NationalId: "",
                FirstName: "",
                LastName: "",
                EmailAddress: "",
                CarRegistrationNumber: selectedVehicleObj?.VehicleRegistrationId || "",
                // Wifi details- can be added later if needed
                /* SsId: "",
                BssId: "",
                WifiToggleStatus: 0, */
            };

            const response = await MapBasedBooking_api(params);
            /* handleApiResponse(response, setNotification, t, {
                successMsg: 0,
                errorMsg: 1
            }); */
            handleApiUiResponse(response, {
                successToaster: 0,
                errorToaster: 1,
                alertPopup: 1
            });
            if (response?.Data?.ResponseCode === 200) {
                const bookingStatusreqParams = {
                    ZoneId: bookingZoneId,
                    MapBookingQueueId: response?.Data?.Records?.MapBookingQueueId,
                    IsGuest: 0,    // ONLY for New Booking
                    BookingDate: formatDate(selectedDate),
                    // FROM SELECTED VEHICLE
                    VehicleTypeId: selectedVehicleObj?.SizeId || 0,
                    VehicleFuelId: selectedVehicleObj?.FuelId || 0,
                    VehicleAccessibleId: selectedVehicleObj?.AccessibleId || 0,
                    VehicleShareableId: selectedVehicleObj?.ShareableId || 0,
                };
                const statusResult = await pullBookingStatus(bookingStatusreqParams);
            }
            setIsLoading(false);

        } catch (error) {

            console.error("Booking API error", error);
            setIsLoading(false);
        }
        finally { setIsLoading(false); }
    };

    const pullBookingStatus = async (reqParams) => {

        const maxAttempts = 3;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {

            // 3s before every retry, whatever ended the previous attempt
            // (still processing, no spot yet, or a failed request).
            if (attempt > 1) {
                await new Promise(res => setTimeout(res, 3000));
            }

            try {

                const response = await MapBasedBooking_apiBookingStatus(reqParams);
                const records = response?.Data?.Records || {};
                const spotId = records?.SpotId ?? null;
                const isPastBooking = records?.IsPastBooking ?? 0;
                const errorCode = response?.Data?.ResponseCode;
                handleApiUiResponse(response, {
                    successToaster: 1,
                    errorToaster: 1,
                    alertPopup: 1,
                    onConfirmByCode: {
                        603: async () => {
                            if (!reqParams.ZoneId || !selectedDate) return;
                            //await fetchBookings(selectedZoneId, formatDate(selectedDate));
                        }
                    }
                });
                if (errorCode === 423) {
                    localStorage.clear();
                    sessionStorage.clear();
                    // history.push("/Login", { replace: true });
                    return { stopFetch: true };
                }

                /* if (errorCode === 416 || errorCode === 427 || errorCode === 605) {
                    return { stopFetch: true };
                } */
                if (errorCode !== 200 && errorCode !== 1555) {
                    return { stopFetch: true };
                }
                if (Number(spotId) === -1) {
                    // Still processing: check again on the next attempt.
                    continue;
                }

                if (Number(spotId) > 0) {

                    /* handleApiResponse(response, setNotification, t, {
                        successMsg: 1,
                        errorMsg: 1
                    }); */
                    /* handleApiUiResponse(response, {
                        successToaster: 1,
                        errorToaster: 1,
                        alertPopup: 1
                    }); */
                    if (Number(isPastBooking) === 1) {

                        const res = await PostBookingQuestion_api({
                            ZoneId: reqParams.ZoneId
                        });

                        if (res?.Data?.ResponseCode === 200) {

                            const questions = res?.Data?.Records?.QuestionAnswerList || [];

                            setPostRequestParams({
                                ZoneId: reqParams.ZoneId,
                                ClaimDate: formatDate(selectedDate),
                                StartTime: "",
                                EndTime: "",
                                SpotId: spotId,
                                ClaimId: response?.Data?.Records?.LastId_tblparkingrelease,
                                CompanyId: res?.Data?.Records?.CompanyId,
                                IsGuest: reqParams.IsGuest
                            });

                            setPostbookingQueModalData(questions);
                            setShowPostbookingQueModal(true);

                            return { stopFetch: true };
                        }
                    }
                    else { history.push("/booking"); }
                    return { stopFetch: false };
                }

            } catch (error) {
                console.error("BookingStatus_api error:", error);
            }
        }

        return { stopFetch: false };
    };

    return (
        <div className="tab-content active">
            {isLoading && (
                <div id='preloader'>

                    <img src={loadingGIF} alt="" />
                </div>
            )}
            <StepProgress step={step} activeTab="parking" />
            {/* STEP 1 */}
            {step === 0 && (
                <div className="step active" data-step="0">
                    <form>

                        {/* Zone */}
                        <div className=" mb-4">
                            <label className="popup-inner-title mt-0">{t("zone")}</label>
                            <CustomCheckboxDropdown
                                items={bookingInfo?.ZoneList || []}
                                value={selectedZoneIds}
                                labelKey="ZoneName"
                                valueKey="Id"
                                placeholder={t("select_a_zone")}
                                noOptionsMessage={t("no_zone_available")}
                                searchable={false}
                                multiple
                                onChange={(ids) => setSelectedZoneIds((prev) => applyZoneSelection(prev, ids))}
                            />
                        </div>

                        {/* Date */}
                        <div className="date-picker-wrapper  mb-4">
                            <label className="popup-inner-title mt-0">{t("date")}</label>
                            <CustomDatePicker
                                id="datepicker"
                                selected={selectedDate}
                                onChange={(d) => {
                                    const newDate = d ? d : new Date();
                                    // reset selected time
                                    setSelectedFrom("00:00");
                                    setSelectedTo("00:00");
                                    setSelectedDate(newDate);
                                }}
                                minDate={new Date()} // optional: disallow past dates
                                maxDate={maxDate}
                                onKeyDown={(e) => e.preventDefault()}
                                onChangeRaw={(e) => e.preventDefault()}
                            />
                        </div>

                        {/* Type */}
                        <div className=" mb-4">
                            <label className="popup-inner-title mt-0">{t("react_type_label")}</label>

                            <CustomSearchableDropdown
                                key={`type-${selectedZoneIds.join(",")}`}
                                items={typeList}
                                value={selectedType}
                                labelKey="TypeName"
                                valueKey="TypeId"
                                searchable={false}
                                onChange={(value) => setSelectedType(value)}
                            />


                        </div>

                        {/* Time */}
                        {selectedType === 2 && (
                            <div className="custom-timepicker ">
                                <label className="popup-inner-title">
                                    {t("react_select_time")}
                                </label>

                                <div
                                    className={`selected-box bg-white ${selectedFrom && selectedTo && !(selectedFrom === "00:00" && selectedTo === "00:00")
                                        ? "ctm-success"
                                        : ""
                                        }`} >
                                    <div
                                        onClick={() => {
                                            setActiveField("from");
                                            setShowSlots(true);
                                        }}
                                    >
                                        {t("from")} <span className={selectedFrom === "00:00" ? "text-grey" : ""}>{selectedFrom}</span>
                                    </div>

                                    <div className="text-center">
                                        <img src={Images.RightBlackArrow} className="img-fluid" />
                                    </div>

                                    <div
                                        onClick={() => {
                                            setActiveField("to");
                                            setShowSlots(true);
                                        }}
                                    >
                                        {t("react_to")} <span className={selectedTo === "00:00" ? "text-grey" : ""}>{selectedTo}</span>
                                    </div>
                                </div>
                                {showSlots && (
                                    <div className="slot-container d-grid mb-3">

                                        {timePoints.map((time, idx) => {

                                            const isDisabled = false;
                                            const hasFromValue = selectedFrom && selectedFrom !== "00:00";
                                            const hasToValue = selectedTo && selectedTo !== "00:00";
                                            const pointMinutes = toMinutes(time);
                                            const fromMinutes = hasFromValue ? toMinutes(selectedFrom) : null;
                                            const toMinutesValue = hasToValue ? toMinutes(selectedTo) : null;

                                            const isSelected =
                                                hasFromValue &&
                                                (
                                                    time === selectedFrom ||
                                                    (hasToValue && pointMinutes >= fromMinutes && pointMinutes <= toMinutesValue)
                                                );

                                            return (
                                                <div
                                                    key={idx}
                                                    className={`slot ${isDisabled ? "disabled" : ""} ${isSelected ? "active" : ""}`}
                                                    onClick={() => {
                                                        if (!isDisabled) handleTimePointClick(time);
                                                    }}
                                                >
                                                    {time}
                                                </div>
                                            );
                                        })}

                                    </div>
                                )}

                                {showStep1TimeValidation && timeValidationMessage && (
                                    <div className="error-text">
                                        <img src={Images.ErrorMsg} className="img-fluid" />
                                        <p>{timeValidationMessage}</p>
                                    </div>
                                )}


                            </div>
                        )}

                        {/* Vehicle */}
                        {(selectedTab === 1 && vehicleList.length > 0) && (
                            <div className="mb-4" ref={vehicleRef}>

                                <label className="popup-inner-title mt-0">{t("vehicle")}</label>

                                <CustomSearchableDropdown
                                    key={`vehicle-${selectedZoneIds.join(",")}`}
                                    items={vehicleList}
                                    value={selectedVehicle}
                                    labelKey="VehicleRegistrationId"
                                    valueKey="VehiclePrimaryKey"
                                    searchable={false}
                                    noOptionsMessage={t("there_is_no_car_plate")}
                                    onChange={(value) => {

                                        setSelectedVehicle(value);

                                        const vehicle = vehicleList.find(
                                            v => v.VehiclePrimaryKey === value
                                        );

                                        // error message is handled by the validation effect;
                                        // here we only scroll to it on the user's selection
                                        if (vehicle?.isDisabledVehicle) {
                                            vehicleRef.current?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "center"
                                            });
                                        }

                                    }}

                                />

                                {errors.vehicle && (
                                    <div className="error-text">
                                        <img src={Images.ErrorMsg} className="img-fluid" />
                                        <p>{errors.vehicle}</p>
                                    </div>
                                )}

                            </div>
                        )}

                        {/* Tags */}
                        {spotTagList?.length > 0 && (
                            <div className="">
                                <label className="popup-inner-title mt-0">{t("tags")}</label>
                                <div className="inline-btn-tags tags-btn d-flex flex-wrap">
                                    {spotTagList?.map((tag) => (

                                        <button
                                            key={tag.Id}
                                            type="button"
                                            className={selectedTags.includes(tag.Id) ? "selected" : ""}
                                            onClick={() => toggleTag(tag.Id)}
                                        >
                                            {tag.Name}
                                        </button>

                                    ))}

                                </div>
                            </div>
                        )}

                    </form>
                </div>
            )}

            {/* STEP 2 */}
            {step === 1 && (
                <div className="step active" data-step="1">

                    <form>
                        {/* Space */}
                        <div className="">
                            <label className="popup-inner-title mt-0">{t("react_spots")} </label>
                            {/* Spaces grouped under their zone's name (RON-5769). */}
                            <CustomZoneWiseSpotDropdownTickmark
                                items={displaySpacesList}
                                value={selectedSpace}
                                labelKey="SpotName"
                                valueKey="Id"
                                groupKey="ZoneId"
                                groupLabelKey="ZoneName"
                                searchable={false}
                                disabled={displaySpacesList.length === 0}
                                placeholder={
                                    displaySpacesList.length === 0
                                        ? t("react_no_spaces")
                                        : t("react_select_space")
                                }
                                onChange={(value) => {
                                    // RON-6204: a new space starts a new time selection. The
                                    // range and the confirmation details belonged to the previous
                                    // space. A time chosen on step 1 is kept: the spaces were
                                    // already filtered by it.
                                    if (value !== selectedSpace && selectedType === 2 && !timeSelectedInStep1) {
                                        setSelectedFrom("00:00");
                                        setSelectedTo("00:00");
                                        setCheckedPreBookingQue(false);
                                        setShowStep2TimeValidation(false);
                                    }

                                    setSelectedSpace(value);

                                    const selectedSpotObj = displaySpacesList.find(
                                        (item) => item.Id === value
                                    );

                                    if (selectedSpotObj) {
                                        setIsRoomHasApprover(selectedSpotObj.IsRoomHasApprover || 0);

                                        // ⚠️ Not available in your API per spot → keep 0
                                        setIsFullyDayBlocked(selectedSpotObj.IsFullyDayBlocked || 0);
                                    }
                                }}

                            />

                            {/* <img src={Images.RollIconGray} alt="" className="map-icon" /> */}
                        </div>
                        {isStep2NoSpaceAvailable && (
                            <div className="no-space-found text-center mt-3">

                                <img
                                    src={Images.NoSpaceError}
                                    className="img-fluid mb-2"
                                    style={{ maxWidth: "160px" }}
                                />

                                <p className="text-red fs-5">
                                    {t("react_no_availability")}
                                </p>

                                <p className="text-dark-gray">
                                    {t("react_modify_criteria")}
                                </p>

                            </div>
                        )}

                        {(selectedType === 2 && displaySpacesList.length > 0 && (!timeSelectedInStep1 || isTimeMissing)) && (
                            <div className="custom-timepicker mt-3">

                                <label className="popup-inner-title">
                                    {t("react_select_time")}
                                </label>

                                <div className="slot-container d-grid mb-3">

                                    {timeSlots.map((slot, idx) => {

                                        const [start, end] = slot.split("-");

                                        const isDisabled = isStep2SlotDisabled(slot);

                                        const isSelected =
                                            selectedFrom &&
                                            selectedTo &&
                                            slotOverlapsRange(start, end, selectedFrom, selectedTo);

                                        return (
                                            <div
                                                key={idx}
                                                className={`slot ${isDisabled ? "disabled" : ""} ${isSelected ? "active" : ""}`}
                                                onClick={() => {
                                                    if (!isDisabled) handleConsecutiveSlotClick(slot);
                                                }}
                                            >
                                                {slot}
                                            </div>
                                        );
                                    })}

                                </div>

                                {showStep2TimeValidation && (isTimeMissing || isStep2RangeInvalid) && (
                                    <div className="error-text">
                                        <img src={Images.ErrorMsg} className="img-fluid" />
                                        <p>{t("please_select_timeslot")}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="participants-list ctm-checklist">
                            {(!!preBookingQuestion && displaySpacesList.length > 0) && (
                                <div className={`form-check bg-white ${checkedPreBookingQue ? "check-active" : ""}`}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="preBookingQuestion"
                                        checked={checkedPreBookingQue}
                                        onChange={(e) => setCheckedPreBookingQue(e.target.checked)}
                                    />
                                    <label
                                        className="form-check-label ms-2"
                                        htmlFor={`preBookingQuestion`}
                                    >
                                        <span dangerouslySetInnerHTML={{ __html: safeHtml }} />

                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Booking summary — shown once a space is selected (merged from the old confirm step) */}
                        {/* RON-6204: hourly shows it only with a valid range for this space. */}
                        {(displaySpacesList.length > 0 && selectedSpace &&
                            !(selectedType === 2 && (isTimeMissing || isStep2RangeInvalid))) && (
                            <>
                            <div className="d-flex align-items-start justify-content-between">

                                <div className="icon-with-list mb-0">

                                    <div className="icon-wrap mb-3">
                                        <img src={Images.Participant} className="img-fluid" />
                                        <h4>{FirstName} {LastName}</h4>
                                    </div>

                                    <div className="icon-wrap mb-3">
                                        <img src={Images.GreenCalender} className="img-fluid" alt="date" />
                                        <h4>
                                            {displaySelectedDate}
                                        </h4>
                                    </div>
                                    {(selectedFrom !== "00:00" && selectedTo !== "00:00" && selectedFrom !== "" && selectedTo !== "") && (

                                        <div className="icon-wrap mb-3">
                                            <img src={Images.TimeIcon} className="img-fluid" />
                                            <h4>
                                                {t("from")} <span className="zone_text">{selectedFrom}</span> {t("react_to")} <span className="zone_text">{selectedTo}</span>
                                            </h4>
                                        </div>
                                    )}

                                    <div className="icon-wrap mb-3 align-items-start">
                                        <img src={Images.ZoneMap} className="img-fluid" />
                                        <h4>{selectedSpaceObj?.ZoneName || bookingZoneObj?.ZoneName || ""}
                                            <span className="zone_text d-block">{" " + (selectedSpaceObj?.SpotName || "")}</span>

                                        </h4>
                                    </div>

                                </div>

                                {/* Show CREDIT */}
                                {zoneCreditValue > 0 && (
                                    <div className="date-with-credit d-flex align-items-start justify-content-end pe-3">
                                        <a
                                            href="#"
                                            className="orange-outline-with-icon"
                                            onClick={(e) => e.preventDefault()}
                                        >
                                            {individualDayCost === 0 || individualDayCost === "0" ? (
                                                <>
                                                    <img src={Images.GrayStar} alt="star" />
                                                    <span>{individualDayCost}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <img src={Images.OrangeStar} alt="star" />
                                                    <span>
                                                        {(`-${individualDayCost}`)}
                                                    </span>
                                                </>
                                            )}
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="text-center booking-complete-img">
                                <img src={Images.BookingCompleted} className="img-fluid"></img>
                            </div>
                            </>
                        )}


                    </form>
                </div>
            )}
            {/* Single persistent footer — buttons toggle by step, container never remounts (prevents button repaint) */}
            <div className={`actions d-flex ${step > 0 ? "justify-content-between" : "justify-content-end"} mt-3`}>
                {step > 0 && (
                    <button
                        type="button"
                        className="main-btn outline-gray-btn"
                        onClick={handleStep2Back}
                    >
                        {t("back")}
                    </button>
                )}
                {step === 0 && (
                    <button
                        type="button"
                        className={`main-btn fill-green-btn ${errors.vehicle || !hasSelectedZones || shouldDisableNextButton ? "green-disabled" : ""}`}
                        disabled={!!errors.vehicle || !hasSelectedZones || shouldDisableNextButton}
                        onClick={handleStep1Next}
                    >
                        {t("next")}
                    </button>
                )}
                {step === 1 && (
                    <button
                        type="button"
                        className={`main-btn fill-green-btn ${isStep2NextDisabled ? "green-disabled" : ""}`}
                        disabled={isStep2NextDisabled}
                        onClick={handleConfirmBooking}
                    >
                        {t("confirm")}
                    </button>
                )}
            </div>
            {/* {(notification && !isLoading) && ( */}
            {notification && (
                <NotificationMessage
                    type={notification.type}
                    title={notification.title}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
            {showPostbookingQueModal && (
                <PostbookingQueModal
                    questions={postbookingQueModalData}
                    onClose={() => {
                        setShowPostbookingQueModal(false);
                        history.push("/booking");
                    }}
                    onSubmit={async () => {

                        setShowPostbookingQueModal(false);
                        history.push("/booking");

                    }}
                    postRequestParams={postRequestParams}
                    selectedBookingDate={selectedDate || postRequestParams?.ClaimDate}
                />
            )}
            {showNoVehiclePopup && (
                <NoVehiclePopUp
                    variant="orange"
                    message={t("react_parking_space_booking")}
                    onClose={() => setShowNoVehiclePopup(false)}
                    onConfirm={() => setShowNoVehiclePopup(false)}
                />
            )}
            {renderApiUi(isLoading)}
        </div>
    );
};

export default BookParkingOrDesk;
